import React from "react";
import { useScrollReveal } from "./useAnimations";
import "./HowWeWork.scss";

/* ── Step SVG icons ── */
const IconSearch = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);
const IconStethoscope = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4v6a4 4 0 008 0V4" />
    <line x1="4" y1="4" x2="4" y2="2" />
    <line x1="12" y1="4" x2="12" y2="2" />
    <circle cx="18" cy="14" r="2" />
    <path d="M18 16v2a4 4 0 01-4 4h-2" />
  </svg>
);
const IconCompass = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polygon
      points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"
      fill="currentColor"
    />
  </svg>
);
const IconCheckCircle = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const IconDoc = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const stepIcons = [IconSearch, IconStethoscope, IconCompass, IconCheckCircle];

const steps = [
  {
    num: "01",
    title: "Discover",
    desc:
      "We align on objectives, regulatory exposure, stakeholder constraints and success criteria before a line of work is commissioned.",
    deliverable: "Engagement Charter",
  },
  {
    num: "02",
    title: "Diagnose",
    desc:
      "We assess your technology posture, architecture health, security gaps, compliance exposure, cost inefficiencies and delivery capability.",
    deliverable: "Prioritised Diagnostic Brief",
  },
  {
    num: "03",
    title: "Decide",
    desc:
      "We present structured options with cost, timeline and risk transparency \u2014 enabling confident, board-level decision-making.",
    deliverable: "Board-Ready Decision Pack",
  },
  {
    num: "04",
    title: "Deliver",
    desc:
      "We execute defined outcomes within fixed-scope boundaries \u2014 with measurable milestones, transparent governance and senior oversight throughout.",
    deliverable: "Fixed-Scope Outcome Release",
  },
];

const StepCard = ({ step, index, total }) => {
  const [ref, vis] = useScrollReveal({ threshold: 0.1 });
  const Icon = stepIcons[index];

  return (
    <div
      ref={ref}
      className={`how__step${vis ? " is-visible" : ""}`}
      style={{ transitionDelay: `${index * 0.15}s` }}
    >
      {/* Timeline node */}
      <div className="how__timeline-node">
        <div className="how__step-icon-circle">
          <Icon />
        </div>
        <span className="how__step-num">{step.num}</span>
        {index < total - 1 && <div className="how__timeline-line" />}
      </div>

      {/* Content */}
      <div className="how__step-content">
        <h3 className="how__step-title">{step.title}</h3>
        <p className="how__step-desc">{step.desc}</p>
        <div className="how__step-tag">
          <IconDoc />
          <span>{step.deliverable}</span>
        </div>
      </div>
    </div>
  );
};

const HowWeWork = () => {
  const [hRef, hVis] = useScrollReveal();
  const [imgRef, imgVis] = useScrollReveal({ threshold: 0.15 });

  return (
    <section className="how">
      <div className="how__inner">
        {/* Header */}
        <div className="how__top">
          <div ref={hRef} className="how__header">
            <span className={`how__label${hVis ? " is-visible" : ""}`}>
              How We Work
            </span>
            <h2 className={`how__title${hVis ? " is-visible" : ""}`}>
              From Uncertainty to Execution - in Weeks, Not Quarters
            </h2>
            <p className={`how__subtitle${hVis ? " is-visible" : ""}`}>
              digiRyte operates on a compressed advisory model. Every engagement
              is chartered, diagnosed, decided and delivered within a defined
              commercial boundary. Boards get clarity. Teams get direction.
              Projects get built.
            </p>
          </div>

          <div
            ref={imgRef}
            className={`how__hero-img${imgVis ? " is-visible" : ""}`}
          >
            <img
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=680&q=80"
              alt="Team collaboration around a whiteboard"
              loading="lazy"
            />
          </div>
        </div>

        {/* Vertical timeline steps */}
        <div className="how__steps">
          {steps.map((s, i) => (
            <StepCard key={s.num} step={s} index={i} total={steps.length} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowWeWork;
